import { ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import type {
	AdminDashboardCardConfig,
	AdminDashboardCardSize,
	AdminDashboardCardStats,
} from '../adminDashboardConfig';

type AdminDashboardStatCardProps = {
	config: AdminDashboardCardConfig;
	size: AdminDashboardCardSize;
	stats?: AdminDashboardCardStats | null;
};

const sizeStyles: Record<
	AdminDashboardCardSize,
	{
		card: string;
		iconWrap: string;
		icon: string;
		title: string;
		primary: string;
		secondary: string;
		chevron: string;
		layout: 'stack' | 'inline';
	}
> = {
	large: {
		card: 'p-5 min-h-[10.5rem]',
		iconWrap: 'size-11 rounded-xl',
		icon: 'size-5',
		title: 'text-sm font-bold',
		primary: 'text-3xl font-black',
		secondary: 'text-xs',
		chevron: 'size-4',
		layout: 'stack',
	},
	medium: {
		card: 'p-3.5 min-h-[6.5rem]',
		iconWrap: 'size-9 rounded-lg',
		icon: 'size-4',
		title: 'text-xs font-bold',
		primary: 'text-xl font-black',
		secondary: 'text-[11px]',
		chevron: 'size-3.5',
		layout: 'stack',
	},
	compact: {
		card: 'p-2.5 min-h-[3.25rem]',
		iconWrap: 'size-7 rounded-lg',
		icon: 'size-3.5',
		title: 'text-[11px] font-bold',
		primary: 'text-sm font-black',
		secondary: 'text-[10px]',
		chevron: 'size-3',
		layout: 'inline',
	},
};

const iconWrapClassName = 'bg-stone-50 text-stone-600 ring-1 ring-stone-100';

export const AdminDashboardStatCard = ({
	config,
	size,
	stats,
}: AdminDashboardStatCardProps) => {
	const navigate = useNavigate();
	const Icon = config.icon;
	const styles = sizeStyles[size];
	const hasStats = stats != null && stats.primary.length > 0;

	return (
		<article
			role="button"
			tabIndex={0}
			onClick={() => navigate(config.path)}
			onKeyDown={(event) => {
				if (event.key === 'Enter' || event.key === ' ') {
					event.preventDefault();
					navigate(config.path);
				}
			}}
			className={cn(
				'group flex cursor-pointer flex-col rounded-[1.25rem] border border-stone-100 bg-white shadow-sm transition-all hover:border-stone-200 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-300',
				styles.card,
				styles.layout === 'inline' && 'justify-center',
			)}
		>
			{styles.layout === 'stack' ? (
				<>
					<div className="flex items-start justify-between gap-2">
						<div
							className={cn(
								'flex shrink-0 items-center justify-center',
								styles.iconWrap,
								iconWrapClassName,
							)}
						>
							<Icon
								className={styles.icon}
								strokeWidth={2.25}
								aria-hidden
							/>
						</div>
						<ChevronRight
							className={cn(
								'shrink-0 text-stone-300 transition-colors group-hover:text-stone-500',
								styles.chevron,
							)}
							aria-hidden
						/>
					</div>

					<h3
						className={cn(
							'font-albert mt-3 text-stone-900',
							styles.title,
						)}
					>
						{config.title}
					</h3>

					{hasStats ? (
						<>
							<p
								className={cn(
									'mt-1 tabular-nums leading-none text-stone-900',
									styles.primary,
								)}
							>
								{stats.primary}
							</p>
							{stats.secondary.length > 0 ? (
								<ul className="mt-2 space-y-0.5">
									{stats.secondary.map((item) => (
										<li
											key={item}
											className={cn(
												'font-medium leading-snug text-stone-400',
												styles.secondary,
											)}
										>
											{item}
										</li>
									))}
								</ul>
							) : null}
						</>
					) : null}
				</>
			) : (
				<div className="flex items-center gap-2.5">
					<div
						className={cn(
							'flex shrink-0 items-center justify-center',
							styles.iconWrap,
							iconWrapClassName,
						)}
					>
						<Icon
							className={styles.icon}
							strokeWidth={2.25}
							aria-hidden
						/>
					</div>

					<div className="min-w-0 flex-1">
						<h3
							className={cn(
								'font-albert truncate text-stone-900',
								styles.title,
							)}
						>
							{config.title}
						</h3>
						{hasStats ? (
							<p
								className={cn(
									'mt-0.5 tabular-nums leading-none text-stone-500',
									styles.primary,
								)}
							>
								{stats.primary}
							</p>
						) : null}
					</div>

					<ChevronRight
						className={cn(
							'shrink-0 text-stone-300 transition-colors group-hover:text-stone-500',
							styles.chevron,
						)}
						aria-hidden
					/>
				</div>
			)}
		</article>
	);
};
